<template>
  <main class="flex-grow w-full flex justify-center py-4 sm:py-6 px-3 sm:px-4">
    <div class="w-full max-w-screen-xl mx-auto">
      <div class="w-full">
        <!-- grid only, no flex -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <!-- LEFT COLUMN: nav + form stacked -->
          <div class="flex flex-col space-y-6 sm:space-y-8">
            <nav class="flex items-center space-x-2 sm:space-x-4 overflow-x-auto">
              <div :class="stepClass(1)" @click="goToStep(1)">
                {{ $t("checkout.steps.step1.label") }}
                <span v-if="isStep1Incomplete" class="ml-1 sm:ml-2 badge badge-warning text-xs">!</span>
              </div>
              <div :class="stepClass(2)" @click="goToStep(2)">
                {{ $t("checkout.steps.step2.label") }}
              </div>
            </nav>

            <AccountCreationForm v-if="checkoutStore.currentStep === 1" />
            <CustomerDetailsForm v-if="checkoutStore.currentStep === 2" :billing-interval="billingInterval" />
          </div>

          <!-- RIGHT COLUMN: order summary -->
          <aside
            :aria-label="$t('components.checkout.orderSummary')"
            :class="[
              'sticky top-20 sm:top-24 bg-white rounded-lg',
              checkoutStore.currentStep === 2 ? 'hidden lg:block' : 'block',
            ]">
            <PlanSelectionSummary
              :selectedPlanOptions="selectedPlanOptions"
              :cta-link="signupLink"
              :billing-interval="billingInterval"
              :disable-footer="true"
              @update:billingInterval="billingInterval = $event"
              @checkout="handleCheckout" />
          </aside>
        </div>
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";

import PlanSelectionSummary from "@/features/planSelection/components/PlanSelectionSummary.vue";
import { usePlanSelectionViewModel } from "@/features/planSelection/composables/usePlanSelectionViewModel";
import CustomerDetailsForm from "@/features/checkout/components/CustomerDetailsForm.vue";
import AccountCreationForm from "@/features/checkout/components/AccountCreationForm.vue";
import { useCheckoutStore } from "@/features/checkout/stores/checkoutStore";
import { useDomainSelectionStore } from "@/features/domain-checker-input/stores/useDomainSelectionStore";
import { usePlanSelectionStore } from "@/features/planSelection/stores/usePlanSelectionStore";
import { useI18n } from "vue-i18n";

const route = useRoute();
const domainStore = useDomainSelectionStore();
const planStore = usePlanSelectionStore();
const { locale } = useI18n();

const rawInterval = route.query.interval?.toString()?.toLowerCase();
const defaultInterval = rawInterval === "yearly" ? "yearly" : "monthly";
const billingInterval = ref<"monthly" | "yearly">(defaultInterval);

// Handle URL parameters
onMounted(() => {
  // Handle domain parameter
  const domainParam = route.query.domain?.toString();
  if (domainParam) {
    domainStore.domainName = domainParam;
    domainStore.isAvailable = false; // Assume transfer for URL param domains
    domainStore.flowState = "available";
  }
  
  // Handle plans parameter
  const plansParam = route.query.plans?.toString();
  if (plansParam) {
    const planIds = plansParam.split(',').map(id => id.trim()).filter(Boolean);
    if (planIds.length > 0) {
      planStore.setUrlPreselectedPlans(planIds);
    }
  }
  
  // Handle language parameter
  const langParam = route.query.lang?.toString();
  if (langParam && (langParam === 'nl' || langParam === 'en')) {
    locale.value = langParam;
  }
});

const { selectedPlanOptions, signupLink } = usePlanSelectionViewModel(billingInterval);
const checkoutStore = useCheckoutStore();

// Track whether user has ever reached step 2
const hasReachedStep2 = ref(false);

function goToStep(step: number) {
  if (step === 2) hasReachedStep2.value = true;
  checkoutStore.goToStep(step);
}

// Show warning badge on step 1 once user has visited step 2 until email is filled
const isStep1Incomplete = computed(() => hasReachedStep2.value && !checkoutStore.form.email.trim());

function stepClass(step: number) {
  return [
    "px-3 sm:px-4 py-2 font-medium text-sm sm:text-base cursor-pointer transition-colors duration-200",
    checkoutStore.currentStep === step ? "text-primary border-b-2 border-primary" : "text-gray-500 hover:text-gray-700",
  ].join(" ");
}

function handleCheckout() {
  onSubmit();
}

function onSubmit() {
  if (!validate()) return;
}

function validate() {
  return false;
}
</script>

<style>
@keyframes bounce-slow {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}
.animate-bounce-slow {
  animation: bounce-slow 2s infinite;
}
</style>
